import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Typography,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from "@mui/material";
import { Delete, Book } from "@mui/icons-material";
import { uploadImage, clearMedia } from "../../actions/mediaActions";
import { fetchAllCategories } from "../../actions/categoryActions";
import SortableList from '../Common/SortableList/SortableList';
import {
  fetchOneSubcategory,
  addEditAudioBook,
  deleteSubCategory,
  clearAllSubCategories,
} from "../../actions/subcategoryActions";
import ImageContainerPreview from '../Common/ImageContainerPreview';
import LoadingBackdrop from "../Common/LoadingBackdrop";
import Swal from "sweetalert2";
import { preventEnterFormSubmission } from '../../utils/formUtils';

const AddEditAudioBook = forwardRef(({ isEdit, selectedId }, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const all_categories = useSelector(state => state.categories.all_categories);
  const current_subcategory = useSelector(state => state.subcategories.current_subcategory);

  const [state, setState] = useState({
    subcategory_id: null,
    subcategory_title: "",
    subcategory_description: "",
    subcategory_is_active: true,
    subcategory_image: "",
    subcategory_image_preview: "",
    subcategory_text_page_url: "",
    subcategory_text_image_url: "",
    subcategory_text_image_url_preview: "",
    subcategory_is_dark_mode: false,
    subcategory_is_book: true,
    category: 0,
    sessions: [],
    quotes: [],
    isSetToEdit: false,
    sessionData: {},
    sortOrder: []
  });

  useEffect(() => {
    dispatch(fetchAllCategories());
    dispatch(clearMedia());
    dispatch(clearAllSubCategories());

    if (isEdit) {
      dispatch(fetchOneSubcategory(selectedId));
    }
  }, [dispatch, isEdit, selectedId]);

  useEffect(() => {
    if (
      isEdit &&
      current_subcategory &&
      !state.isSetToEdit
    ) {
      setSubCategoryForEditing(current_subcategory);
    }
  }, [isEdit, current_subcategory, state.isSetToEdit]);

  const setSubCategoryForEditing = useCallback((current_subcategory) => {
    if (!current_subcategory) {
      return;
    }

    const sessions = current_subcategory.sessions || [];
    const sessionData = {};
    const sortOrder = [];

    sessions.forEach(session => {
      sessionData[session.session_id] = {
        is_featured: !!session.session_is_featured,
        is_dark_mode: !!session.session_is_dark_mode
      };
      sortOrder.push(session.session_id.toString());
    });

    setState(prev => ({
      ...prev,
      subcategory_id: current_subcategory.subcategory_id,
      subcategory_title: current_subcategory.subcategory_title || '',
      subcategory_description: current_subcategory.subcategory_description || '',
      subcategory_is_active: !!current_subcategory.subcategory_is_active,
      subcategory_image: current_subcategory.subcategory_image || '',
      subcategory_image_preview: current_subcategory.subcategory_image || '',
      subcategory_text_page_url: current_subcategory.subcategory_text_page_url || '',
      subcategory_text_image_url: current_subcategory.subcategory_text_image_url || '',
      subcategory_text_image_url_preview: current_subcategory.subcategory_text_image_url || '',
      subcategory_is_dark_mode: !!current_subcategory.subcategory_is_dark_mode,
      subcategory_is_book: !!current_subcategory.subcategory_is_book,
      category: current_subcategory.category_id || 0,
      sessions: sessions,
      quotes: current_subcategory.quotes || [],
      isSetToEdit: true,
      sessionData,
      sortOrder
    }));
  }, []);

  const handleSessionDataChange = useCallback((id, data) => {
    setState(prev => ({
      ...prev,
      sessionData: {
        ...prev.sessionData,
        [id]: {
          is_featured: !!data.is_featured,
          is_dark_mode: !!data.is_dark_mode
        }
      }
    }));
  }, []);

  const handleFieldChange = useCallback((e) => {
    const name = e.target.name;
    const value = e.target.value;
    setState(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleImageUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (state.subcategory_image_preview) {
      window.URL.revokeObjectURL(state.subcategory_image_preview);
    }
    setState(prev => ({
      ...prev,
      subcategory_image_preview: window.URL.createObjectURL(file),
    }));
    dispatch(uploadImage(file))
      .then((response) => {
        setState(prev => ({
          ...prev,
          subcategory_image: response.data.image_url,
        }));
        dispatch(clearMedia());
      });
  }, [state.subcategory_image_preview, dispatch]);

  const handleDeepLinkImageUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (state.subcategory_text_image_url_preview) {
      window.URL.revokeObjectURL(state.subcategory_text_image_url_preview);
    }
    setState(prev => ({
      ...prev,
      subcategory_text_image_url_preview: window.URL.createObjectURL(file),
    }));
    dispatch(uploadImage(file))
      .then((response) => {
        setState(prev => ({
          ...prev,
          subcategory_text_image_url: response.data.image_url,
        }));
        dispatch(clearMedia());
      });
  }, [state.subcategory_text_image_url_preview, dispatch]);

  const removeQuote = useCallback((index) => {
    setState(prev => ({
      ...prev,
      quotes: prev.quotes.filter((_, i) => i !== index)
    }));
  }, []);

  const addQuote = useCallback((e) => {
    e.preventDefault();
    setState(prev => ({
      ...prev,
      quotes: [...prev.quotes, { speaker: "", quote_text: "" }]
    }));
  }, []);

  const submit = useCallback(() => {
    Swal.fire({
      title: "Saving...",
      animation: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
    });

    const {
      subcategory_id,
      subcategory_title,
      subcategory_description,
      subcategory_is_active,
      subcategory_image,
      subcategory_text_page_url,
      subcategory_text_image_url,
      subcategory_is_dark_mode,
      subcategory_is_book,
      quotes,
      category: category_id,
      sessionData,
      sortOrder,
    } = state;

    const subcategory_json = {
      subcategory_id,
      subcategory_title,
      subcategory_description,
      subcategory_is_active,
      subcategory_image,
      subcategory_text_page_url,
      subcategory_text_image_url,
      subcategory_is_dark_mode,
      subcategory_is_book,
    };

    const filteredQuotes = (quotes || [])
      .filter((obj) => obj.quote_text !== "")
      .map(quote => ({
        quote_text: quote.quote_text,
        speaker: quote.speaker || ""
      }));

    const formattedSessions = (sortOrder || []).map(sessionId => ({
      session_id: sessionId,
      session_is_featured: !!sessionData[sessionId]?.is_featured,
      session_is_dark_mode: !!sessionData[sessionId]?.is_dark_mode
    }));

    const options = {
      quotes_json: JSON.stringify(filteredQuotes),
      category_id: category_id,
      subcategory_json: JSON.stringify(subcategory_json),
      session_ids_json: JSON.stringify(formattedSessions),
    };

    dispatch(addEditAudioBook(options, () => navigate('/audiobooks')));
  }, [state, dispatch]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    submit();
  }, [submit]);

  const handleDelete = useCallback((e) => {
    e.preventDefault();
    Swal.fire({
      icon: "warning",
      text: "Are you sure you want to delete this category?",
      showConfirmButton: true,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "Delete Category",
    }).then((result) => {
      if (result.value) {
        dispatch(deleteSubCategory(state.subcategory_id, () => navigate('/audiobooks')));
      }
    });
  }, [state.subcategory_id, dispatch, navigate]);

  // Expose submit method to parent
  useImperativeHandle(ref, () => ({
    submit
  }), [submit]);

  if (!all_categories) {
    return <LoadingBackdrop open={true} />;
  }

  // Transform sessions into the format expected by SortableList
  const sortableItems = state.sortOrder.map(sessionId => {
    const session = (state.sessions || []).find(s => s.session_id.toString() === sessionId.toString());
    return {
      id: sessionId.toString(),
      label: session?.session_name || '',
      isChapter: !!session?.session_is_chapter
    };
  });

  return (
    <Box component="form" onSubmit={handleSubmit} onKeyDown={preventEnterFormSubmission} autoComplete="off">
      <Box display="grid" gap={4}>
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, 1fr)' }} gap={3}>
          <Box>
            <TextField
              fullWidth
              label="AudioBook Name"
              name="subcategory_title"
              value={state.subcategory_title}
              onChange={handleFieldChange}
              required
            />
          </Box>
          <Box>
            <FormControl fullWidth>
              <InputLabel id="category-select-label">Category</InputLabel>
              <Select
                labelId="category-select-label"
                id="category-select"
                name="category"
                value={state.category}
                label="Category"
                onChange={handleFieldChange}
              >
                <MenuItem value={0}>None</MenuItem>
                {all_categories && Object.values(all_categories)
                  .filter(category => category.category_is_audiobook)
                  .map((category) => (
                    <MenuItem key={category.category_id} value={category.category_id}>
                      {category.category_title}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      name="subcategory_is_active"
                      checked={!!state.subcategory_is_active}
                      onChange={(e) => setState(prev => ({ ...prev, subcategory_is_active: e.target.checked }))}
                    />
                  }
                  label="Active"
                />
              </Box>
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      name="subcategory_is_dark_mode"
                      checked={!!state.subcategory_is_dark_mode}
                      onChange={(e) => setState(prev => ({ ...prev, subcategory_is_dark_mode: e.target.checked }))}
                    />
                  }
                  label="Dark Mode"
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box>
          <TextField
            fullWidth
            label="Description"
            name="subcategory_description"
            value={state.subcategory_description}
            onChange={handleFieldChange}
            multiline
            rows={3}
            required
          />
        </Box>

        <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
          <Box>
            <Typography variant="h6" gutterBottom>
              AudioBook Image
            </Typography>
            <ImageContainerPreview
              title="AudioBook Image"
              image={state.subcategory_image_preview || state.subcategory_image}
              name="subcategory_image"
              handleRemove={() => setState(prev => ({ ...prev, subcategory_image: "", subcategory_image_preview: null }))}
              handleImageUpload={handleImageUpload}
              showCopyUrlButton={true}
              sx={{ padding: 1, background: '#FFFCF5', height: 90, objectFit: 'contain' }}
            />
          </Box>

          {/* Deep Link Settings box now spans 2 columns */}
          <Box gridColumn={{ md: 'span 2' }}>
            <Typography variant="h6" gutterBottom>
              Deep Link Settings
            </Typography>
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 2fr' }} gap={3}>
              <ImageContainerPreview
                title="Deep Link Image"
                image={state.subcategory_text_image_url_preview || state.subcategory_text_image_url}
                name="deep_link_image"
                handleRemove={() => setState(prev => ({ ...prev, subcategory_text_image_url: "", subcategory_text_image_url_preview: null }))}
                handleImageUpload={handleDeepLinkImageUpload}
                showCopyUrlButton={true}
                sx={{ padding: 1, background: '#FFFCF5', height: 90, objectFit: 'contain' }}
              />
              <TextField
                fullWidth
                label="Deep Link URL"
                name="subcategory_text_page_url"
                value={state.subcategory_text_page_url}
                onChange={handleFieldChange}
              />
            </Box>
          </Box>
        </Box>

        {isEdit && !!state.sessions && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Book /> Chapters / Sessions
            </Typography>
            <Paper sx={{ p: 2 }}>
              <SortableList
                items={sortableItems}
                sessionData={state.sessionData}
                onSessionDataChange={handleSessionDataChange}
                onSortEnd={({ oldIndex, newIndex }) => {
                  const newOrder = [...state.sortOrder];
                  const [removed] = newOrder.splice(oldIndex, 1);
                  newOrder.splice(newIndex, 0, removed);
                  setState(prev => ({ ...prev, sortOrder: newOrder }));
                }}
              />
            </Paper>
          </Box>
        )}

        <Box>
          <Typography variant="h6" gutterBottom>
            Quotes
          </Typography>
          {state.quotes.map((quote, index) => (
            <Box key={index} display="flex" gap={2} sx={{ mb: 2, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                label="Quote Text"
                value={quote.quote_text}
                onChange={(e) => {
                  const newQuotes = [...state.quotes];
                  newQuotes[index] = { ...quote, quote_text: e.target.value };
                  setState(prev => ({ ...prev, quotes: newQuotes }));
                }}
                multiline
                rows={2}
              />
              <Tooltip title="Remove Quote">
                <IconButton
                  color="error"
                  onClick={() => removeQuote(index)}
                  sx={{ mt: 1 }}
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
          <Button
            variant="contained"
            onClick={addQuote}
            sx={{ mt: 2 }}
          >
            Add Quote
          </Button>
        </Box>

        {isEdit && (
          <Box sx={{ textAlign: 'right' }}>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
            >
              Delete AudioBook
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
});

AddEditAudioBook.displayName = 'AddEditAudioBook';

export default AddEditAudioBook;
